"use client";

import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { login, logout } from "@/services/users/authService";
import { LoginPayload } from "@/types/auth";
import {
  clearTokens,
  setupTokenRefresh,
  stopTokenRefresh,
  isAuthenticated as checkIsAuthenticated,
  getInitialAuthState,
} from "@/lib/utils/auth";
import { useQueryClient } from "@tanstack/react-query";

export const useAuth = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  // Initialize token refresh on mount if authenticated
  useEffect(() => {
    const authState = getInitialAuthState();
    if (authState.hasClientCookie) {
      setupTokenRefresh();
    }

    // Cleanup on unmount
    return () => {
      stopTokenRefresh();
    };
  }, []);

  const loginMutation = useMutation({
    mutationFn: (payload: LoginPayload) => login(payload),
    onSuccess: () => {
      // Token refresh is already set up in login service
      // Navigation will be handled by the component with redirect parameter
    },
    onError: (error) => {
      console.error("로그인 실패:", error);
    },
  });

  const handleLogin = (payload: LoginPayload, redirectTo?: string) => {
    loginMutation.mutate(payload, {
      onSuccess: () => {
        // Redirect to the original page or home page
        // Use router.push for client-side navigation to prevent hydration mismatches
        const finalRedirect = redirectTo || "/";
        
        // Refresh the router to ensure middleware re-evaluates auth state
        router.refresh();
        
        // Navigate to the target page
        router.push(finalRedirect);
      },
    });
  };

  const handleLogout = async () => {
    // Stop token refresh first
    stopTokenRefresh();

    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      try {
        await queryClient.cancelQueries();
      } catch (e) {
        console.warn("Failed to cancel queries on logout:", e);
      }

      try {
        queryClient.clear();
      } catch (e) {
        console.warn("Failed to clear query cache on logout:", e);
      }

      // Clear tokens and cookies (already done in logout service, but ensure it's done)
      clearTokens();

      // Use router.push for client-side navigation to prevent hydration mismatches
      // Refresh the router to ensure middleware re-evaluates auth state
      router.refresh();
      
      // Navigate to login page
      router.push("/login");
    }
  };

  return {
    login: handleLogin,
    isLoading: loginMutation.isPending,
    error: loginMutation.error,
    logout: handleLogout,
    isLoggedIn: checkIsAuthenticated(),
  };
};
